import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Heart, Crown, Coffee } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOOKUP_META = {
    tip_small:    { icon: Coffee, label: "Tipped", color: "#FFD600" },
    tip_medium:   { icon: Coffee, label: "Tipped", color: "#00BCD4" },
    tip_large:    { icon: Coffee, label: "Tipped", color: "#00E676" },
    pro_lifetime: { icon: Crown,  label: "Pro",    color: "#FF5722" },
};

export default function ThanksPage() {
    const [data, setData] = useState({ supporters: [], count: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/thanks`);
                setData(data);
            } catch (e) {
                setData({ supporters: [], count: 0 });
            }
            setLoading(false);
        })();
    }, []);

    return (
        <div className="min-h-screen grain relative bg-[#F8F9FA]">
            <header className="max-w-5xl mx-auto px-6 md:px-10 pt-8 pb-6 flex items-center justify-between relative z-10">
                <Link to="/" className="btn-pill" data-testid="back-home">
                    <ArrowLeft size={18} /> Back to ClearCursor
                </Link>
                <div className="font-display font-extrabold text-xl text-slate-900">
                    ClearCursor
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 md:px-10 pb-16 relative z-10">
                <div className="card-bento" style={{ background: "#FFF7E0" }}>
                    <span className="tag">Community</span>
                    <h1 className="font-display font-extrabold text-4xl md:text-6xl leading-tight text-slate-900 mt-4">
                        Hall of Thanks <Heart className="inline-block ml-2" size={44} color="#FF5722" fill="#FF5722" />
                    </h1>
                    <p className="text-lg md:text-xl text-slate-800 mt-4 max-w-3xl">
                        These wonderful people keep ClearCursor free for everyone. If
                        you found the tool useful, you can join them by leaving a tip
                        or unlocking Pro — you'll be asked at checkout whether you
                        want to be listed here.
                    </p>
                </div>

                <div className="mt-8 card-bento">
                    {loading && (
                        <div className="text-center py-12 text-slate-700 text-lg">
                            Loading supporters…
                        </div>
                    )}
                    {!loading && data.count === 0 && (
                        <div className="text-center py-12" data-testid="thanks-empty">
                            <div className="font-display font-extrabold text-3xl text-slate-900">
                                Be the very first ✨
                            </div>
                            <p className="text-slate-700 mt-3 text-lg max-w-xl mx-auto">
                                Nobody has opted in yet. Grab the founding-supporter
                                spot — tip $3 or unlock Pro, and your first name will
                                live right here.
                            </p>
                            <Link to="/#support" className="btn-pill solid mt-6 inline-flex">
                                <Coffee size={18} /> Support the project
                            </Link>
                        </div>
                    )}
                    {!loading && data.count > 0 && (
                        <>
                            <div className="text-slate-700 text-base mb-6">
                                <strong className="font-display font-extrabold text-slate-900 text-2xl">
                                    {data.count}
                                </strong>{" "}
                                {data.count === 1 ? "supporter" : "supporters"} — thank you all 🧡
                            </div>
                            <ul
                                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4"
                                data-testid="thanks-list"
                            >
                                {data.supporters.map((s, i) => {
                                    const meta = LOOKUP_META[s.lookup_key] || LOOKUP_META.tip_medium;
                                    const Icon = meta.icon;
                                    return (
                                        <li
                                            key={i}
                                            className="card-bento"
                                            style={{ padding: "18px", background: "#fff" }}
                                            data-testid="thanks-item"
                                        >
                                            <div
                                                className="w-10 h-10 rounded-full border-2 border-slate-900 flex items-center justify-center"
                                                style={{ background: meta.color, boxShadow: "2px 2px 0 #0f172a" }}
                                            >
                                                <Icon size={20} color="#0f172a" />
                                            </div>
                                            <div className="font-display font-extrabold text-xl text-slate-900 mt-3 truncate">
                                                {s.first_name}
                                            </div>
                                            <div className="text-xs text-slate-600 mt-1 uppercase tracking-wider font-bold">
                                                {meta.label}
                                            </div>
                                            {s.note && (
                                                <div className="text-sm text-slate-700 mt-2 italic">
                                                    "{s.note}"
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}
